import * as React from 'react';
import { connect } from 'react-redux';

import StatusLabel from 'components/status-label/StatusLabel';
import ActionButton from 'components/buttons/action-button/ActionButton';
import InputCheckbox from 'components/inputs/input-checkbox/InputCheckbox';
import MediaPreview from 'components/MediaPreview';
import { withModal } from 'components/modal/ModalHOC';
import OutfitCreateModal from '../outfit-create-modal/OutfitCreateModal';

import OutfitReviewModal from 'modules/realm-outfits/components/outfit-review-modal/OutfitReviewModal';
import { STATUS_LABEL_MAPPING } from 'modules/realm-outfits/components/realm-outfits-table/RealmOutfitsTable';

import DeliverableContentContext from 'modules/deliverables/components/DeliverableContentContext';

import { isAdmin, isCcmModerator, isInfluencerRealm } from 'utils/account';
import { WITH_GALLERY_MODAL_CONFIG } from 'utils/gallery';
import { __t, applyTranslations } from 'utils/language';
import { getDisplayDate } from 'utils/date';
import { prepareDeliverableAttachmentsForGallery } from 'utils/deliverables';
import {
  REALM_OUTFIT_STATUS_APPROVED,
  REALM_OUTFIT_STATUS_DECLINED,
  REALM_OUTFIT_STATUS_DRAFT,
  REALM_OUTFIT_STATUS_OFFLINE,
  REALM_OUTFIT_STATUS_PENDING,
  REALM_OUTFIT_STATUS_PUBLISHED,
  REALM_OUTFIT_STATUS_UNPUBLISHED
} from 'utils/realm-outfits';

import './outfit-media-to-publish.scss';

applyTranslations(require('./outfit-media-to-publish.i18n.json'));
applyTranslations(require('locales/common/collabary-common.i18n.json'));

import { Campaign, RealmOutfit, SubcampaignInfluencerProfile } from 'types';

type OwnProps = {
  campaign?: Campaign,
  outfit: RealmOutfit,
  outfitPosition: number,
  creatorWmcLink?: string | null,
  deliverableId?: number,
  offerId?: number,
  isForBrand?: boolean,
  isCampaignCreatorless?: boolean,
  influencerProfile?: SubcampaignInfluencerProfile,
  maxSKUs?: number,
  minSKUs?: number,
  onSelectMoveOutfitToAnotherDeliverable?: (outfit: RealmOutfit, checked: boolean) => void
};


type StateProps = {
  hasOutfitRelatedRole: boolean
};

type Props = OwnProps & StateProps;

const msgKey = 'outfit_media_to_publish';
const COMMON_MSG_KEY = 'collabary_common';

const REVIEW_STATUSES = [
  REALM_OUTFIT_STATUS_DECLINED,
  REALM_OUTFIT_STATUS_PENDING,
  REALM_OUTFIT_STATUS_PUBLISHED,
  REALM_OUTFIT_STATUS_UNPUBLISHED,
  REALM_OUTFIT_STATUS_OFFLINE,
  REALM_OUTFIT_STATUS_APPROVED
];

const ActionButtonWithModalReviewOutfit = withModal(ActionButton, {
  modalContent: OutfitReviewModal,
  closeIconPosition: 'corner',
  closeIconType: 'fancy'
});

const ActionButtonWithBrandOutfit = withModal(ActionButton, {
  modalContent: OutfitCreateModal,
  closeIconPosition: 'corner',
  closeIconType: 'fancy'
});

const DivWithModal = withModal(<div />, WITH_GALLERY_MODAL_CONFIG);

const OutfitMediaToPublish = ({
  campaign,
  outfit,
  outfitPosition,
  isForBrand,
  influencerProfile,
  deliverableId,
  creatorWmcLink,
  offerId,
  hasOutfitRelatedRole,
  isCampaignCreatorless,
  maxSKUs,
  minSKUs,
  onSelectMoveOutfitToAnotherDeliverable
}: Props) => {
  const previewWidth = 183;
  const previewHeight = 183;

  if (isForBrand && outfit.status === REALM_OUTFIT_STATUS_DRAFT) {
    return null;
  }

  const galleryImageItems = prepareDeliverableAttachmentsForGallery(outfit.images.map((image, index) => ({
    ...image,
    assetUrl: image.url,
    id: index
  })));

  const galleryVideoItems = outfit.video ? [{
    url: outfit.video.url,
    assetUrl: outfit.video.thumbnailUrl,
    type: 'collabary-video'
  }] : [];

  const influencerFullName = influencerProfile ? `${influencerProfile.influencerFirstName} ${influencerProfile.influencerLastName}` : null;

  return (
    <DivWithModal
      key={`outfit-media-attachment-${outfit.id}`}
      modalContentProps={{
        items: [...galleryImageItems, ...galleryVideoItems],
        currentPosition: 0
      }}
    >
      <div className="outfit-media-to-publish__container">
        <div style={{ width: previewWidth, height: previewHeight }}>
          <MediaPreview
            key={`outfit-media-to-publish-${outfit.id}`}
            attachment={{
              id: outfit.images[0].url,
              url: outfit.images[0].url
            }}
            width={previewWidth}
            height={previewHeight}
            showPdfFileName={false}
          />
        </div>
        <div className="outfit-media-to-publish__outfit-mode">
          <StatusLabel
            size="nano"
            type={STATUS_LABEL_MAPPING[outfit.status]}
            label={__t(`${msgKey}.labels.${outfit.status}`)}
          />
          <div className="outfit-media-to-publish__outfit-info">
            <span>{__t(`${msgKey}.labels.name`)}:</span>
            <span>{outfit.name}</span>
            <span>{__t(`${msgKey}.labels.items`)}:</span>
            <span>{outfit.numberOfItems || '-'}</span>
            <span>{__t(`${msgKey}.labels.created`)}:</span>
            <span>{getDisplayDate(outfit.createdAt)}</span>
            <span>{__t(`${msgKey}.labels.reviewed`)}:</span>
            <span>{getDisplayDate(outfit.reviewedAt) || '-'}</span>
          </div>
        </div>
        {
          onSelectMoveOutfitToAnotherDeliverable && (
            <div
              className="outfit-media-to-publish__outfit-action-container"
              onClick={(event) => {
                event.stopPropagation();
              }}
            >
              <InputCheckbox
                id={`outfit-${outfit.id}`}
                name={`outfit-move-content-${outfit.id}`}
                type="regular"
                checked={outfit.isSelected}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  onSelectMoveOutfitToAnotherDeliverable(outfit, event.target.checked);
                }}
              />
            </div>
          )
        }
        {
          REVIEW_STATUSES.includes(outfit.status)
            && !onSelectMoveOutfitToAnotherDeliverable
            && isForBrand
            && hasOutfitRelatedRole
            && (
              <div
                onClick={(event) => {
                  event.stopPropagation();
                }}
                className="outfit-media-to-publish__outfit-action-container outfit-media-to-publish__outfit-action-container--with-button"
              >
                <DeliverableContentContext.Consumer>
                  {(pageType) => (
                    <ActionButtonWithModalReviewOutfit
                      width="small"
                      height="minimum"
                      style="small"
                      color="primary"
                      modalContentProps={{
                        campaign,
                        creatorWmcLink,
                        outfitPosition,
                        deliverableId,
                        offerId,
                        pageType,
                        influencerName: influencerFullName,
                        triggerType: 'button'
                      }}
                    >
                      {__t(`${COMMON_MSG_KEY}.action_button.review_outfit`)}
                    </ActionButtonWithModalReviewOutfit>
                  )}
                </DeliverableContentContext.Consumer>
              </div>
            )
        }
        {
          isCampaignCreatorless && campaign && (
            <div
              onClick={(event) => {
                event.stopPropagation();
              }}
              className="outfit-media-to-publish__outfit-action-container outfit-media-to-publish__outfit-action-container--with-button"
            >
              <ActionButtonWithBrandOutfit
                width="small"
                height="minimum"
                style="small"
                color="primary"
                modalContentProps={{
                  mode: 'view',
                  campaignId: campaign.id,
                  campaignName: campaign.name,
                  outfitPosition,
                  triggerType: 'button',
                  genders: campaign?.genders || [],
                  maxSKUs,
                  minSKUs
                }}
              >
                {__t(`${COMMON_MSG_KEY}.action_button.view_outfit_details`)}
              </ActionButtonWithBrandOutfit>
            </div>
          )
        }
      </div>
    </DivWithModal>
  );
};

const mapStateToProps = ({ account }): StateProps => ({
  hasOutfitRelatedRole: isAdmin(account, account.roles)
    || isCcmModerator(account.roles)
    || isInfluencerRealm(account.roles)
});

export default connect(mapStateToProps)(OutfitMediaToPublish);
